<template>
  <footer class="page-footer" data-theme="summer-sky">
    <section class="footer__recruitment">
      <figure class="avatar">
        <img
          v-if="recruitment.figureImg && recruitment.figureImg.url"
          width="250"
          loading="lazy"
          :src="recruitment.figureImg.url + '?w=250'"
          :alt="recruitment.figureImg.alt ?? ''" />
      </figure>
      <div>
        <BlocksWerkenbijContentArea
          v-if="recruitment.rte"
          :content="{ propertyName: null, value: recruitment.rte }" />
        <LinksIconTextLink
          v-if="recruitment.phoneNumber"
          :link="`tel:${recruitment.phoneNumber}`"
          title="telefoon"
          icon="arrow-right"
          icon-color="white"
          :text="recruitment.phoneNumber"
          text-color="white"
          :has-font-bold="true"
          :has-animate-icon="true"
          :has-hover-underline="true" />

        <LinksIconTextLink
          v-if="recruitment.mail"
          :link="`mailto:${recruitment.mail}`"
          title="Email"
          icon="arrow-right"
          icon-color="white"
          :text="recruitment.mail"
          text-color="white"
          :has-font-bold="true"
          :has-animate-icon="true"
          :has-hover-underline="true" />
      </div>
    </section>
    <section class="footer__contact">
      <ImagesSvg class="footer__logo" icon="logo-solid" :width="254" />
      <address>
        <BlocksWerkenbijContentArea
          v-if="contact.rte"
          :content="{ propertyName: null, value: contact.rte }" />
      </address>
      <ul class="social-links">
        <li v-for="(social, index) in contact.socialLinks" :key="social.link?.url ?? index">
          <NuxtLink
            v-if="social.link && social.link.url"
            :to="social.link.url"
            :title="social.link.name"
            :target="social.target">
            <ImagesSvg
              :icon="iconMatch(social.iconName ?? '')"
              :alt="`${social.link.name} icon`"
              :width="18"
              :heigth="18" />
          </NuxtLink>
        </li>
      </ul>
    </section>
    <section class="footer__legal" data-theme="navy-blue">
      <ul>
        <li v-for="(link, index) in legal" :key="link.url ?? index">
          <NuxtLink v-if="link.url" :to="link.url" :target="link.target">{{ link.name }}</NuxtLink>
        </li>
      </ul>
    </section>
  </footer>
</template>

<script lang="ts" setup>
import type {
  ImageResponseModel,
  LinkResponseModel,
  SocialLinkBlockResponseModel,
  XhtmlStringResponseModel,
} from '@api';
import { type Icon, isIcon } from '@base/types/icon';

interface MainFooter {
  mainFooter: {
    recruitment: {
      figureImg: ImageResponseModel | null;
      rte: XhtmlStringResponseModel | null;
      phoneNumber: string | null;
      mail: string | null;
    };
    contact: {
      rte: XhtmlStringResponseModel | null;
      socialLinks: SocialLinkBlockResponseModel[] | null;
    };
    legal: LinkResponseModel[] | null;
  };
}

const { mainFooter } = defineProps<MainFooter>();
const { recruitment, contact, legal } = mainFooter;

const iconMatch = (icon: string): Icon => {
  if (!isIcon(icon)) {
    return 'arrow-right';
  }

  return icon;
};
</script>

<style lang="scss" scoped>
.page-footer {
  display: grid;
  grid-template: 'footerRecruitment' 'footerContact' 'footerLegal';
  grid-template-columns: 1fr;
  grid-template-rows: auto auto auto;
  grid-area: pageFooter;
  color: var(--theme-color);
  background-color: var(--theme-bg-color);

  a {
    color: inherit;
  }

  .footer {
    &__logo {
      margin-bottom: px(24);
      margin-left: px(-4);
    }

    &__recruitment {
      position: relative;
      display: grid;
      grid-template: 'recruitmentText recruitmentAvatar';
      grid-template-columns: 2fr 1fr;
      grid-area: footerRecruitment;
      gap: px(20);
      padding: px(36) px(20);
      border-bottom: px(1) solid var(--color-navy-blue);

      .avatar {
        grid-area: recruitmentAvatar;
        height: px(90);
        width: px(90);
        align-self: center;
      }

      :deep(p) {
        margin-top: 0;
        margin-bottom: px(16);
      }

      :deep(h3) {
        color: inherit;
      }
    }

    &__contact {
      position: relative;
      display: block;
      padding: px(36) px(20);
      grid-area: footerContact;

      :deep(a) {
        color: inherit;
      }

      address {
        margin-bottom: px(32);

        :deep(p) {
          margin-top: 0;
        }
      }

      .social-links {
        display: flex;

        a {
          display: grid;
          border-radius: 50%;
          border: px(1) solid transparent;
          place-items: center center;
          width: 100%;
          height: 100%;
          line-height: px(32);
          transition: all 0.2s;
          text-decoration: none;

          &:hover {
            border-color: currentColor;
            transform: scale(1.1);
          }
        }

        li {
          display: block;
          line-height: px(32);
          border: px(1) solid currentColor;
          width: px(32);
          height: px(32);
          margin: px(4);
          border-radius: 50%;
          box-sizing: content-box;

          img {
            flex-shrink: 0;
          }
        }
      }
    }

    &__legal {
      padding: px(12) px(20);
      grid-area: footerLegal;
      color: var(--theme-color);
      background-color: var(--theme-bg-color);

      ul {
        display: flex;
        flex-wrap: wrap;
        justify-content: flex-start;

        /* stylelint-disable-next-line no-descending-specificity -- TODO: this is bugged, maybe remove this rule? */
        li:has(a) {
          display: block;
          position: relative;
          padding: px(8);

          &:not(&:last-of-type)::after {
            content: '|';
            position: absolute;
            right: 0;
            color: var(--color-blue);
          }
        }
      }
    }
  }

  @include media-breakpoint-up(lg) {
    grid-template:
      '. footerRecruitment footerContact .'
      'footerLegal footerLegal footerLegal footerLegal';
    grid-template-columns: 1fr 5fr 5fr 1fr;
    grid-template-rows: auto auto;
    padding-top: px(100);
    align-content: start;
    align-items: start;

    .footer {
      &__recruitment {
        border-bottom: 0;
        padding: px(20) px(60) px(12);
        margin-bottom: px(36);

        &::before {
          left: 0;
        }

        &::after {
          right: 0;
        }

        &::before,
        &::after {
          content: '';
          display: block;
          position: absolute;
          top: 0;
          width: px(1);
          height: px(180);
          background-color: var(--color-navy-blue);
        }

        .avatar {
          width: px(148);
          height: px(148);
        }
      }

      &__contact {
        padding: px(20) px(60) px(12);
        margin-bottom: px(36);

        &::after {
          content: '';
          display: block;
          position: absolute;
          top: 0;
          right: 0;
          width: px(1);
          height: px(180);
          background-color: var(--color-navy-blue);
        }
      }

      &__legal {
        display: grid;
        grid-template-columns: 1fr 10fr 1fr;
        grid-template-areas: '. legalLinks .';
        padding: px(32) 0 px(20);

        ul {
          grid-row: 1;
          grid-column: 2;

          li:has(a) {
            padding: px(16);
          }
        }
      }
    }
  }
}
</style>
